import React, {useEffect, useRef, useState} from 'react';
import {CREATION_STEPS, getStepStatuses} from '../../creation';
import {CHARACTER_STATUS} from '../../character';
import {saveDraft, updateStatus} from '../../characterRepository';
import {vtmT, currentLanguage} from '../../i18n';
import VtmCharacterSheet from '../VtmCharacterSheet';
import {StepErrors} from './CreationParts';
import {ConceptStep, ConvictionsStep} from './IdentitySteps';
import {ClanStep, PredatorStep} from './ChoiceSteps';
import {AttributesStep, SkillsStep} from './TraitSteps';
import {DisciplinesStep} from './DisciplinesStep';
import {AdvantagesStep} from './AdvantagesStep';
import '../../../styles/vtm/sheet.css';
import '../../../styles/vtm/roll.css';
import '../../../styles/vtm/creation.css';

const AUTOSAVE_DELAY_MS = 800;

const STEP_COMPONENTS = {
  concept: ConceptStep,
  clan: ClanStep,
  predator: PredatorStep,
  attributes: AttributesStep,
  skills: SkillsStep,
  disciplines: DisciplinesStep,
  advantages: AdvantagesStep,
  convictions: ConvictionsStep,
};

const SummaryStep = ({character, name, sheet, statuses, goTo, onSubmit, submitting}) => {
  const incomplete = CREATION_STEPS.filter(step => step !== 'summary' && !statuses[step].valid);
  return (
    <div className="vtmSummaryStep">
      {incomplete.length > 0 ? (
        <div className="vtmStepErrors">
          {vtmT('incompleteSteps', {steps: ''})}
          <div className="vtmIncompleteLinks">
            {incomplete.map(step => (
              <button key={step} type="button" className="vtmButton small" onClick={() => goTo(step)}>{vtmT(`step_${step}`)}</button>
            ))}
          </div>
        </div>
      ) : (
        <button type="button" className="vtmButton primary large" onClick={onSubmit} disabled={submitting}>{vtmT('submit')}</button>
      )}
      <div className="vtmSummaryPreview">
        <VtmCharacterSheet character={{...character, name, vtm: sheet}} />
      </div>
    </div>
  );
};

/**
 * Assistant de création : 9 étapes, brouillon enregistré automatiquement dans Firestore.
 * Le personnage reste au statut « brouillon » jusqu'à l'envoi au MJ.
 */
const VtmCreationWizard = ({character}) => {
  const lng = currentLanguage();
  const [name, setName] = useState(character.name || '');
  const [sheet, setSheet] = useState(character.vtm);
  const [stepIndex, setStepIndex] = useState(0);
  const [saveState, setSaveState] = useState('saved');
  const [submitting, setSubmitting] = useState(false);
  const firstRender = useRef(true);
  const pending = useRef(null);
  const panelRef = useRef(null);

  // Enregistrement automatique après une courte pause dans la saisie
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    setSaveState('saving');
    pending.current = {name, vtm: sheet};
    const timer = setTimeout(() => {
      saveDraft(character.uid, pending.current)
        .then(() => {
          pending.current = null;
          setSaveState('saved');
        })
        .catch(e => console.log(e));
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [name, sheet, character.uid]);

  // Ne rien perdre si on quitte la page pendant la pause
  useEffect(() => () => {
    if (pending.current) saveDraft(character.uid, pending.current).catch(e => console.log(e));
  }, [character.uid]);

  const statuses = getStepStatuses(name, sheet);
  const stepId = CREATION_STEPS[stepIndex];
  const StepComponent = STEP_COMPONENTS[stepId];

  const goToIndex = (index) => {
    setStepIndex(Math.min(CREATION_STEPS.length - 1, Math.max(0, index)));
    if (panelRef.current) {
      panelRef.current.focus();
      if (panelRef.current.scrollIntoView) panelRef.current.scrollIntoView({block: 'start'});
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await saveDraft(character.uid, {name, vtm: sheet});
      pending.current = null;
      await updateStatus(character.uid, CHARACTER_STATUS.PENDING);
    } catch (e) {
      console.log(e);
      setSubmitting(false);
    }
  };

  return (
    <div className="vtmWizard">
      <div className="vtmWizardHeader">
        <div>
          <h1 className="vtmWizardTitle">{vtmT('creationTitle')}</h1>
          <p className="vtmWizardSubtitle">
            {name || vtmT('newCharacter')} · {vtmT('startingProfile', {generation: sheet.generation, bloodPotency: sheet.bloodPotency})}
          </p>
        </div>
        <span className="vtmSaveState" aria-live="polite">{saveState === 'saving' ? vtmT('saving') : vtmT('saved')}</span>
      </div>

      <nav className="vtmStepNav" aria-label={vtmT('creationTitle')}>
        <ol>
          {CREATION_STEPS.map((step, index) => {
            const valid = statuses[step].valid;
            return (
              <li key={step}>
                <button
                  type="button"
                  className={`vtmStepperItem${index === stepIndex ? ' current' : ''}${valid ? ' valid' : ''}`}
                  aria-current={index === stepIndex ? 'step' : undefined}
                  onClick={() => goToIndex(index)}
                >
                  <span className="vtmStepperNumber" aria-hidden="true">{valid && step !== 'summary' ? '✓' : index + 1}</span>
                  <span className="vtmStepperLabel">{vtmT(`step_${step}`)}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <section className="vtmStepPanel" ref={panelRef} tabIndex={-1} aria-labelledby="vtmStepTitle">
        <h2 id="vtmStepTitle" className="vtmStepTitle">{stepIndex + 1}. {vtmT(`step_${stepId}`)}</h2>
        <p className="vtmStepHelp">{vtmT(`stepHelp_${stepId}`)}</p>

        {StepComponent ? (
          <StepComponent name={name} setName={setName} sheet={sheet} change={setSheet} lng={lng} />
        ) : (
          <SummaryStep
            character={character}
            name={name}
            sheet={sheet}
            statuses={statuses}
            goTo={step => goToIndex(CREATION_STEPS.indexOf(step))}
            onSubmit={submit}
            submitting={submitting}
          />
        )}

        {stepId !== 'summary' && <StepErrors stepId={stepId} status={statuses[stepId]} lng={lng} />}
      </section>

      <div className="vtmWizardFooter">
        <button type="button" className="vtmButton ghost" onClick={() => goToIndex(stepIndex - 1)} disabled={stepIndex === 0}>{vtmT('previous')}</button>
        {stepIndex < CREATION_STEPS.length - 1 && (
          <button type="button" className="vtmButton primary" onClick={() => goToIndex(stepIndex + 1)}>{vtmT('next')}</button>
        )}
      </div>
    </div>
  );
};

export default VtmCreationWizard;
